import { isString } from 'lodash';
import {
  makeAdvancedReportsApi,
  makeApi,
  makeBenefitsApi,
} from 'src/http/api/internal/internal-http';
import { CollectiblesRepository } from 'src/repositories/benefits/collectibles.repository';
import { CouponsRepository } from 'src/repositories/benefits/coupons.repository';
import { collectibleType, collectionPeriodType, collectionType } from 'src/types/collectibles';
import { locationsTypeItem } from 'src/types/coupons';
import { toBase64 } from 'src/utils/utils';
import { create } from 'zustand';

type States = {
  isLoadingCollectionsList: boolean;
  isLoadingCollectionDetails: boolean;
  isLoadingCollectionCreate: boolean;
  isLoadingCollectionUpdate: boolean;
  isLoadingCollectionPeriods: boolean;
  isLoadingCollectibles: boolean;
  isLoadingCollectibleDetails: boolean;
  isLoadingCollectibleCreate: boolean;
  isLoadingCollectibleUpdate: boolean;
  updatingCollectibleTypeId: string | null;
  isLoadingLocations: boolean;
  collectionsList: collectionType[];
  collectionDetails: collectionType | null;
  collectionPeriods: collectionPeriodType[];
  collectiblesList: collectibleType[];
  collectibleDetails: collectibleType | null;
  locations: locationsTypeItem[];
};

type Actions = {
  getCollectionsList: () => Promise<void>;
  getCollectionDetails: (id: string) => Promise<void>;
  getCollectionPeriods: () => Promise<void>;
  resetCollectionDetails: () => Promise<void>;
  createCollection: (collection: collectionType) => Promise<collectionType | undefined>;
  updateCollection: (id: string, collection: collectionType) => Promise<void>;
  getCollectiblesList: (collectionId: string) => Promise<void>;
  getCollectibleDetails: (id: string) => Promise<void>;
  createCollectible: (id: string, collectible: collectibleType) => Promise<void>;
  updateCollectible: (id: string, collectible: collectibleType) => Promise<void>;
  updateCollectibleType: (id: string, type: collectibleType['type']) => Promise<void>;
  resetCollectibleDetails: () => Promise<void>;
  resetCollectiblesList: () => Promise<void>;
  getLocations: () => Promise<void>;
  exportData: (ids: string[], userName: string) => void;
};

type Store = Actions & States;

const initialValues = {
  isLoadingCollectionsList: false,
  isLoadingCollectionDetails: false,
  isLoadingCollectionCreate: false,
  isLoadingCollectionUpdate: false,
  isLoadingCollectionPeriods: false,
  isLoadingCollectibles: false,
  isLoadingCollectibleDetails: false,
  isLoadingCollectibleCreate: false,
  isLoadingCollectibleUpdate: false,
  updatingCollectibleTypeId: null,
  isLoadingLocations: false,
  collectionsList: [],
  collectionDetails: null,
  collectionPeriods: [],
  collectiblesList: [],
  collectibleDetails: null,
  locations: [],
};

const repository = new CollectiblesRepository(makeBenefitsApi());
const advancedReportsRepository = new CollectiblesRepository(makeAdvancedReportsApi());
const couponsRepository = new CouponsRepository(makeApi());

export const useCollectiblesStore = create<Store>((set) => ({
  ...initialValues,

  getCollectionsList: async () => {
    set({ isLoadingCollectionsList: true });
    const response = await repository.getCollectionsList();
    if (response.isLeft()) {
      set({ collectionsList: [], isLoadingCollectionsList: false });
      throw new Error(response.value.message);
    }
    set({
      collectionsList: response.value,
      isLoadingCollectionsList: false,
    });
  },

  getCollectionDetails: async (id: string) => {
    set({ isLoadingCollectionDetails: true });
    const response = await repository.getCollectionDetails(id);
    if (response.isLeft()) {
      set({ collectionDetails: null, isLoadingCollectionDetails: false });
      throw new Error(response.value.message);
    }
    set({
      collectionDetails: response.value,
      isLoadingCollectionDetails: false,
    });
  },

  getCollectionPeriods: async () => {
    set({ isLoadingCollectionPeriods: true });

    try {
      const response = await repository.getCollectionPeriods();

      if (response.isLeft()) {
        set({ collectionPeriods: [], isLoadingCollectionPeriods: false });
        return;
      }

      set({
        collectionPeriods: Array.isArray(response.value) ? response.value : [],
        isLoadingCollectionPeriods: false,
      });
    } catch {
      set({ collectionPeriods: [], isLoadingCollectionPeriods: false });
    }
  },

  createCollection: async (collection: collectionType) => {
    set({ isLoadingCollectionCreate: true });

    if (!isString(collection.image)) {
      try {
        const imgBase64: any = await toBase64(collection.image);
        const res: any = await couponsRepository.uploadFile({
          name: collection.image.name,
          base64: imgBase64,
        });
        collection.image = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }
    const response = await repository.createCollection(collection);
    if (response.isLeft()) {
      set({ isLoadingCollectionUpdate: false });
      throw new Error(response?.value?.message);
    }
    set({ isLoadingCollectionCreate: false });
    return response.value;
  },

  updateCollection: async (id, collection) => {
    set({ isLoadingCollectionUpdate: true });

    if (!isString(collection.image)) {
      try {
        const imgBase64: any = await toBase64(collection.image);
        const res: any = await couponsRepository.uploadFile({
          name: collection.image.name,
          base64: imgBase64,
        });
        collection.image = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }
    const response = await repository.updateCollection(id, collection);
    if (response.isLeft()) {
      set({ isLoadingCollectionUpdate: false });
      throw new Error(response.value.message);
    }
    set({
      isLoadingCollectionUpdate: false,
    });
  },

  resetCollectionDetails: async () => set({ collectionDetails: null }),

  createCollectible: async (collectionId: string, collectible: collectibleType) => {
    set({ isLoadingCollectibleCreate: true });

    if (!isString(collectible.imagePreview)) {
      try {
        const imgBase64: any = await toBase64(collectible.imagePreview);
        const res: any = await couponsRepository.uploadFile({
          name: collectible.imagePreview.name,
          base64: imgBase64,
        });
        collectible.imagePreview = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (Array.isArray(collectible.additionalImages)) {
      try {
        const uploadedImages = await Promise.all(
          collectible.additionalImages.map(async (img: any) => {
            if (isString(img)) return img;
            const imgBase64: any = await toBase64(img);
            const res: any = await couponsRepository.uploadFile({
              name: img.name,
              base64: imgBase64,
            });
            return res.value.data.url;
          })
        );
        collectible.additionalImages = uploadedImages;
      } catch (error) {
        console.error(error);
      }
    }

    if (!isString(collectible.icon)) {
      try {
        const imgBase64: any = await toBase64(collectible.icon);
        const res: any = await couponsRepository.uploadFile({
          name: collectible.icon.name,
          base64: imgBase64,
        });
        collectible.icon = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    const response = await repository.createCollectible(collectionId, collectible);
    if (response.isLeft()) {
      set({ isLoadingCollectibleCreate: false });
      throw new Error(response.value.message);
    }
    set({
      isLoadingCollectibleCreate: false,
    });
  },

  getCollectibleDetails: async (id: string) => {
    set({ isLoadingCollectibleDetails: true });
    const response = await repository.getCollectibleDetails(id);
    if (response.isLeft()) {
      set({ collectibleDetails: null, isLoadingCollectibleDetails: false });
      throw new Error(response.value.message);
    }
    set({
      collectibleDetails: response.value,
      isLoadingCollectibleDetails: false,
    });
  },

  getLocations: async () => {
    set({ isLoadingLocations: true });
    const response = await repository.getLocations();
    if (response.isLeft()) {
      set({ locations: [], isLoadingLocations: false });
      return;
    }
    const { value } = response;
    const loc = value.map((location: any) => ({ value: location._id, label: location.name }));
    loc.unshift({ value: 'all', label: 'Todos' });
    set({
      locations: loc,
      isLoadingLocations: false,
    });
  },

  getCollectiblesList: async (collectionId: string) => {
    set({ isLoadingCollectibles: true });
    const response = await repository.getCollectiblesList(collectionId);
    if (response.isLeft()) {
      set({ collectiblesList: [], isLoadingCollectibles: false });
      throw new Error(response.value.message);
    }
    set({
      collectiblesList: response.value,
      isLoadingCollectibles: false,
    });
  },

  updateCollectible: async (id: string, collectible: collectibleType) => {
    set({ isLoadingCollectibleUpdate: true });

    if (!isString(collectible.imagePreview)) {
      try {
        const imgBase64: any = await toBase64(collectible.imagePreview);
        const res: any = await couponsRepository.uploadFile({
          name: collectible.imagePreview.name,
          base64: imgBase64,
        });
        collectible.imagePreview = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (Array.isArray(collectible.additionalImages)) {
      try {
        const uploadedImages = await Promise.all(
          collectible.additionalImages.map(async (img: any) => {
            if (isString(img)) return img;
            const imgBase64: any = await toBase64(img);
            const res: any = await couponsRepository.uploadFile({
              name: img.name,
              base64: imgBase64,
            });
            return res.value.data.url;
          })
        );
        collectible.additionalImages = uploadedImages;
      } catch (error) {
        console.error(error);
      }
    }

    if (!isString(collectible.icon)) {
      try {
        const imgBase64: any = await toBase64(collectible.icon);
        const res: any = await couponsRepository.uploadFile({
          name: collectible.icon.name,
          base64: imgBase64,
        });
        collectible.icon = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    const response = await repository.updateCollectible(id, collectible);
    if (response.isLeft()) {
      set({ isLoadingCollectibleUpdate: false });
      throw new Error(response.value.message);
    }
    set({
      isLoadingCollectibleUpdate: false,
    });
  },

  updateCollectibleType: async (id: string, type: collectibleType['type']) => {
    set({ updatingCollectibleTypeId: id });

    try {
      const response = await repository.updateCollectibleType(id, type);
      if (response.isLeft()) {
        throw new Error(response.value.code || response.value.message);
      }

      set((state) => ({
        collectiblesList: state.collectiblesList.map((collectible) =>
          collectible._id === id
            ? {
                ...collectible,
                ...response.value,
                type: response.value?.type || type,
              }
            : collectible
        ),
        collectibleDetails:
          state.collectibleDetails?._id === id
            ? {
                ...state.collectibleDetails,
                ...response.value,
                type: response.value?.type || type,
              }
            : state.collectibleDetails,
      }));
    } finally {
      set({ updatingCollectibleTypeId: null });
    }
  },

  resetCollectibleDetails: async () => set({ collectibleDetails: null }),

  exportData: async (ids: string[], userName: string) => {
    await advancedReportsRepository.exportCollectibles(ids, userName);
  },

  resetCollectiblesList: async () => set({ collectiblesList: [] }),
}));
